import React, { useMemo } from 'react';
import { useUIColors } from '../../hooks/useTheme';

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDateLabel = (iso) => {
  if (!iso) return 'Sin fecha';
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  const today = todayYmd();
  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrow = `${tomorrowDate.getFullYear()}-${String(tomorrowDate.getMonth() + 1).padStart(2, '0')}-${String(
    tomorrowDate.getDate()
  ).padStart(2, '0')}`;
  if (iso === today) return 'Hoy';
  if (iso === tomorrow) return 'Mañana';
  return date.toLocaleDateString('es-CL', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const groupByDate = (list, dir) => {
  const map = new Map();
  [...list]
    .sort((a, b) => {
      const left = String(a.date || '');
      const right = String(b.date || '');
      if (left === right) return String(a.title || '').localeCompare(String(b.title || ''), 'es');
      return dir === 'asc' ? left.localeCompare(right) : right.localeCompare(left);
    })
    .forEach((event) => {
      const key = event.date || 'sin-fecha';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(event);
    });
  return Array.from(map.entries());
};

const ProspectEventsPanel = ({
  events = [],
  executiveOptions = [],
  assigningId = null,
  onAssignOwner,
  onEventClick,
  calendar = null,
  isDarkMode = false,
}) => {
  const ui = useUIColors();
  const today = todayYmd();
  const grouped = useMemo(() => {
    const upcoming = [];
    const past = [];
    (events || []).forEach((event) => {
      const date = String(event.date || '');
      if (date && date < today) past.push(event);
      else upcoming.push(event);
    });
    return {
      upcoming: groupByDate(upcoming, 'asc'),
      past: groupByDate(past, 'desc'),
    };
  }, [events, today]);

  const selectStyle = {
    height: 32,
    padding: '0 8px',
    borderRadius: 8,
    border: `1px solid ${ui.border}`,
    background: ui.inputBg || ui.panel,
    color: ui.text,
    fontSize: 12,
    minWidth: 150,
    cursor: 'pointer',
  };

  const renderGroup = (entries, emptyLabel) => {
    if (!entries.length) {
      return (
        <div style={{ padding: 16, fontSize: 13, color: ui.muted, textAlign: 'center' }}>{emptyLabel}</div>
      );
    }
    return entries.map(([date, rows]) => (
      <div key={date}>
        <div
          style={{
            padding: '8px 14px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: ui.muted,
            background: ui.header,
            borderBottom: `1px solid ${ui.border}`,
          }}
        >
          {formatDateLabel(date)}
        </div>
        {rows.map((event) => {
          const canAssign = event.kind === 'activity' && event.activityId && typeof onAssignOwner === 'function';
          return (
            <div
              key={event.id}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) 170px',
                gap: 10,
                alignItems: 'center',
                padding: '10px 14px',
                borderBottom: `1px solid ${ui.border}`,
                cursor: onEventClick ? 'pointer' : 'default',
              }}
              onClick={() => onEventClick?.(event)}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: event.color || '#2563eb',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 700, color: ui.text }}>{event.title}</span>
                  {event.status ? (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '1px 7px',
                        borderRadius: 999,
                        background: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                        color: ui.muted,
                      }}
                    >
                      {event.status}
                    </span>
                  ) : null}
                </div>
                <div style={{ fontSize: 12, color: ui.muted, marginTop: 3 }}>
                  {event.subtitle || (event.kind === 'quotation' ? 'Cotización' : 'Actividad')}
                </div>
              </div>
              <div onClick={(click) => click.stopPropagation()}>
                {canAssign ? (
                  <select
                    aria-label={`Responsable de ${event.title}`}
                    value={event.owner || ''}
                    disabled={Number(assigningId) === Number(event.activityId)}
                    onChange={(change) => onAssignOwner(event, change.target.value)}
                    style={selectStyle}
                  >
                    <option value="">Sin asignar</option>
                    {executiveOptions
                      .filter((option) => option.value)
                      .map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                  </select>
                ) : (
                  <div style={{ fontSize: 12, color: ui.muted, textAlign: 'right' }}>
                    {event.owner || 'Sin asignar'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    ));
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {calendar}
      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page,
        }}
      >
        <div
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Listado de eventos</h3>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: ui.muted }}>
            {events.length} {events.length === 1 ? 'evento' : 'eventos'} ordenados cronológicamente, con responsable.
          </p>
        </div>
        {renderGroup(grouped.upcoming, 'No hay eventos próximos.')}
        {grouped.past.length ? (
          <>
            <div
              style={{
                padding: '10px 14px',
                fontSize: 12,
                fontWeight: 700,
                color: ui.muted,
                background: ui.header,
                borderTop: `1px solid ${ui.border}`,
              }}
            >
              Anteriores
            </div>
            {renderGroup(grouped.past, 'Sin eventos anteriores.')}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default ProspectEventsPanel;
